import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MetricCard from '../components/MetricCard';
import { usePortal } from '../context/PortalContext';
import { tableService } from '../services/tableService';
import { TableDetails, TableHandHistoryPayload, TableLiveResults } from '../types';
import { formatCurrency, formatDateTime, getGameTypeLabel } from '../utils/formatters';

const LIVE_REFRESH_INTERVAL_MS = 5000;

const renderSuitGlyph = (card?: string): React.ReactNode => {
  const suit = (card || '').slice(-1).toUpperCase();

  switch (suit) {
    case 'S':
      return <>&spades;</>;
    case 'H':
      return <>&hearts;</>;
    case 'D':
      return <>&diams;</>;
    case 'C':
      return <>&clubs;</>;
    default:
      return '?';
  }
};

const getSuitClassName = (card?: string): string => {
  const suit = (card || '').slice(-1).toUpperCase();

  switch (suit) {
    case 'H':
    case 'D':
      return 'portal-playing-card__suit portal-playing-card__suit--red';
    case 'S':
    case 'C':
      return 'portal-playing-card__suit portal-playing-card__suit--dark';
    default:
      return 'portal-playing-card__suit portal-playing-card__suit--dark';
  }
};

const getCardRank = (card?: string): string => {
  const rank = (card || '').slice(0, -1).toUpperCase();
  return rank || '?';
};

const renderPlayingCard = (card: string) => {
  if (!card) {
    return (
      <div className="portal-playing-card portal-playing-card--sm portal-playing-card--empty">
        <span>-</span>
      </div>
    );
  }

  return (
    <div className="portal-playing-card portal-playing-card--sm">
      <span className="portal-playing-card__rank">{getCardRank(card)}</span>
      <span className={getSuitClassName(card)}>{renderSuitGlyph(card)}</span>
    </div>
  );
};

const renderCardRow = (cards: string[]) => {
  if (!cards.length) {
    return <span className="portal-muted portal-history-table__placeholder">Sem cartas</span>;
  }

  return <div className="portal-playing-card-row portal-playing-card-row--compact">{cards.map((card, index) => <React.Fragment key={`${card}-${index}`}>{renderPlayingCard(card)}</React.Fragment>)}</div>;
};

const normalizeSearchText = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

const TableDetailPage: React.FC = () => {
  const { tableId = '' } = useParams();
  const navigate = useNavigate();
  const [table, setTable] = useState<TableDetails | null>(null);
  const [liveResults, setLiveResults] = useState<TableLiveResults | null>(null);
  const [history, setHistory] = useState<TableHandHistoryPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [liveRefreshEnabled, setLiveRefreshEnabled] = useState(true);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<{
    userId: string;
    userName: string;
    currentStack: number;
    totalBuyIn: number;
  } | null>(null);
  const [historySearch, setHistorySearch] = useState('');
  const [error, setError] = useState('');
  const requestIdRef = useRef(0);
  const { profile, selectedClub } = usePortal();
  const canRemovePlayers = Boolean(profile?.id && selectedClub?.owner_id === profile.id);
  const livePlayerCount = liveResults?.players.length ?? table?.currentPlayers ?? 0;
  const filteredHands = useMemo(() => {
    const hands = history?.hands || [];
    const search = normalizeSearchText(historySearch);

    if (!search) {
      return hands;
    }

    return hands.filter((hand) => {
      const haystack = [
        hand.id,
        formatDateTime(hand.played_at),
        hand.community_cards.join(' '),
        hand.pot_amount.toString(),
        hand.rake_amount.toString(),
        hand.winners.map((winner) => winner.user_name).join(' '),
        hand.participants.map((participant) => participant.user_name).join(' '),
      ]
        .join(' ')
        .toLowerCase();

      return normalizeSearchText(haystack).includes(search);
    });
  }, [history?.hands, historySearch]);

  const loadTableData = useCallback(
    async (options: { silent?: boolean } = {}) => {
      if (!tableId) return;

      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      if (options.silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError('');

      try {
        const [tablePayload, livePayload, historyPayload] = await Promise.all([
          tableService.getTable(tableId),
          tableService.getLiveResults(tableId),
          tableService.getHandHistory(tableId, { limit: 8, offset: 0 }),
        ]);

        if (requestIdRef.current !== requestId) {
          return;
        }

        setTable(tablePayload);
        setLiveResults(livePayload);
        setHistory(historyPayload);
        setLastSyncedAt(new Date().toISOString());
      } catch (nextError: any) {
        if (requestIdRef.current === requestId) {
          setError(nextError.message || 'Erro ao carregar detalhe da mesa');
        }
      } finally {
        if (requestIdRef.current === requestId) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [tableId]
  );

  const openLiveView = useCallback(() => {
    if (!tableId) {
      return;
    }

    navigate(`/mesas/${tableId}/ao-vivo`);
  }, [navigate, tableId]);

  const handleRemovePlayer = useCallback(
    async (player: TableLiveResults['players'][number]) => {
      if (!tableId || !canRemovePlayers) {
        return;
      }
      setRemoveTarget({
        userId: player.user_id,
        userName: player.user_name,
        currentStack: player.total_stack_snapshot,
        totalBuyIn: player.total_buy_in,
      });
    },
    [canRemovePlayers, tableId]
  );

  const confirmRemovePlayer = useCallback(async () => {
    if (!tableId || !canRemovePlayers || !removeTarget) {
      return;
    }

    setRemovingUserId(removeTarget.userId);
    setError('');

    try {
      setLiveResults((current) =>
        current
          ? {
              ...current,
              players: current.players.filter((player) => player.user_id !== removeTarget.userId),
            }
          : current
      );

      await tableService.removePlayerFromTable(tableId, removeTarget.userId);
      await loadTableData({ silent: true });
      setRemoveTarget(null);
    } catch (nextError: any) {
      setError(nextError.message || `Erro ao remover ${removeTarget.userName} da mesa`);
      await loadTableData({ silent: true });
    } finally {
      setRemovingUserId(null);
    }
  }, [canRemovePlayers, loadTableData, removeTarget, tableId]);

  const closeRemoveTarget = useCallback(() => {
    if (!removingUserId) {
      setRemoveTarget(null);
    }
  }, [removingUserId]);

  useEffect(() => {
    void loadTableData();
  }, [loadTableData]);

  useEffect(() => {
    if (!tableId || !liveRefreshEnabled) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      if (document.hidden) {
        return;
      }

      void loadTableData({ silent: true });
    }, LIVE_REFRESH_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [loadTableData, liveRefreshEnabled, tableId]);

  useEffect(() => {
    if (!tableId || !liveRefreshEnabled) {
      return undefined;
    }

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        void loadTableData({ silent: true });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loadTableData, liveRefreshEnabled, tableId]);

  return (
    <div className="portal-page-grid">
      <section className="portal-section-card portal-section-card--hero">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Mesa</p>
            <h3>{table?.name || tableId}</h3>
            <p className="portal-muted">Visao ao vivo da mesa, resultados e historico recente.</p>
          </div>
          <div className="portal-section-card__header-actions">
            <button
              type="button"
              className="portal-secondary-button portal-secondary-button--compact"
              onClick={openLiveView}
              disabled={!tableId}
            >
              Abrir mesa
            </button>
            <span className="portal-chip">
              {lastSyncedAt ? `Ultimo sync ${formatDateTime(lastSyncedAt)}` : 'Sem sync ainda'}
            </span>
            <button
              type="button"
              className={`portal-secondary-button portal-secondary-button--compact portal-toggle-button ${
                liveRefreshEnabled ? 'portal-toggle-button--active' : ''
              }`}
              aria-pressed={liveRefreshEnabled}
              onClick={() => setLiveRefreshEnabled((current) => !current)}
            >
              {liveRefreshEnabled ? 'Tempo real: ligado' : 'Tempo real: desligado'}
            </button>
            <button
              type="button"
              className="portal-secondary-button portal-secondary-button--compact"
              onClick={() => void loadTableData()}
              disabled={loading || refreshing}
            >
              {loading || refreshing ? 'Atualizando...' : 'Atualizar agora'}
            </button>
          </div>
        </div>
        <div className="portal-table-detail-hero">
          <div className="portal-table-detail-hero__item">
            <span>Jogadores</span>
            <strong>
              {livePlayerCount}/{table?.maxPlayers || 0}
            </strong>
          </div>
          <div className="portal-table-detail-hero__item">
            <span>Blinds</span>
            <strong>
              {formatCurrency(table?.smallBlind || 0)} / {formatCurrency(table?.bigBlind || 0)}
            </strong>
          </div>
          <div className="portal-table-detail-hero__item">
            <span>Tempo de acao</span>
            <strong>{table?.actionTimeLimitSeconds || 0}s</strong>
          </div>
          <div className="portal-table-detail-hero__item">
            <span>Jogo</span>
            <strong>{getGameTypeLabel(table?.gameType)}</strong>
          </div>
        </div>
        {error ? <div className="portal-banner portal-banner--error">{error}</div> : null}
        {refreshing ? <div className="portal-banner portal-banner--success">Dados sendo atualizados em tempo real.</div> : null}
      </section>

      <section className="portal-metrics-grid">
        <MetricCard label="Jogo" value={getGameTypeLabel(table?.gameType)} helper="Contrato oficial da mesa" />
        <MetricCard label="Blinds" value={`${formatCurrency(table?.smallBlind || 0)} / ${formatCurrency(table?.bigBlind || 0)}`} />
        <MetricCard label="Jogadores" value={`${livePlayerCount}/${table?.maxPlayers || 0}`} helper="Fila ao vivo da mesa" />
        <MetricCard label="Tempo de acao" value={`${table?.actionTimeLimitSeconds || 0}s`} />
      </section>

      <section className="portal-section-card">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Resultados vivos</p>
            <h3>Saldo atual dos jogadores</h3>
            <p className="portal-muted">A lista mostra somente quem ainda esta na mesa agora.</p>
          </div>
        </div>
        <div className="portal-table-wrap">
          <table className="portal-table">
            <thead>
              <tr>
                <th>Jogador</th>
                <th>Buy-in total</th>
                <th>Stack</th>
                <th>Resultado</th>
                <th>Maos</th>
                <th>Ultima atividade</th>
                {canRemovePlayers ? <th>Acao</th> : null}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={canRemovePlayers ? 7 : 6}>Carregando resultados...</td></tr>
              ) : liveResults?.players.length ? (
                liveResults.players.map((player) => (
                  <tr key={player.user_id}>
                    <td>
                      <strong>{player.user_name}</strong>
                      <div className="portal-muted portal-table__small-meta">
                        {player.is_active ? 'Ativo' : 'Saiu da mesa'}
                      </div>
                    </td>
                    <td>{formatCurrency(player.total_buy_in)}</td>
                    <td>{formatCurrency(player.total_stack_snapshot)}</td>
                    <td className={player.live_result >= 0 ? 'portal-live-result--positive' : 'portal-live-result--negative'}>
                      {formatCurrency(player.live_result)}
                    </td>
                    <td>{player.hands_played}</td>
                    <td>{formatDateTime(player.last_activity_at)}</td>
                    {canRemovePlayers ? (
                      <td>
                        <button
                          type="button"
                          className="portal-danger-button portal-danger-button--compact"
                          onClick={() => void handleRemovePlayer(player)}
                          disabled={removingUserId === player.user_id || !player.is_active}
                        >
                          {removingUserId === player.user_id ? 'Removendo...' : player.is_active ? 'Remover' : 'Indisponível'}
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))
              ) : (
                <tr><td colSpan={canRemovePlayers ? 7 : 6}>Nenhum resultado encontrado.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="portal-section-card">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Historico</p>
            <h3>Ultimas maos</h3>
            <p className="portal-muted">Resumo curto e direto do que aconteceu na mesa.</p>
          </div>
        </div>
        <div className="portal-section-card__header portal-section-card__header--compact">
          <div className="portal-history-search">
            <span className="portal-history-search__label">Buscar mao, jogador, board ou pote</span>
            <input
              type="search"
              className="portal-input portal-history-search__input"
              placeholder="Ex.: 52e99294, Gabriel, K A, R$ 0,30"
              value={historySearch}
              onChange={(event) => setHistorySearch(event.target.value)}
            />
          </div>
          <div className="portal-history-search__counter">
            <span>Mostrando</span>
            <strong>
              {filteredHands.length}/{history?.hands.length || 0}
            </strong>
          </div>
        </div>
        <div className="portal-table-wrap">
          <table className="portal-table portal-history-table">
            <thead>
              <tr>
                <th>Mao</th>
                <th>Data</th>
                <th>Board</th>
                <th>Pote</th>
                <th>Vencedores</th>
                <th>Participantes</th>
              </tr>
            </thead>
            <tbody>
              {filteredHands.length ? (
                filteredHands.map((hand) => (
                  <tr key={hand.id}>
                    <td>
                      <strong>{hand.id.slice(0, 8)}</strong>
                      <div className="portal-muted portal-table__small-meta">
                        {hand.game_type || 'HOLD_EM'}
                      </div>
                    </td>
                    <td>{formatDateTime(hand.played_at)}</td>
                    <td>{renderCardRow(hand.community_cards)}</td>
                    <td>{formatCurrency(hand.pot_amount)}</td>
                    <td>
                      <div className="portal-history-table__stack">
                        {hand.winners.length ? (
                          hand.winners.map((winner) => (
                            <span key={`${hand.id}-${winner.user_id}`}>
                              {winner.user_name} <strong>{formatCurrency(winner.winnings_amount)}</strong>
                            </span>
                          ))
                        ) : (
                          <span className="portal-muted">Nao encontrado</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="portal-history-table__stack">
                        {hand.participants.length ? (
                          hand.participants.slice(0, 4).map((participant) => (
                            <span key={`${hand.id}-${participant.user_id}`}>
                              {participant.user_name} <strong>{formatCurrency(participant.net_result)}</strong>
                            </span>
                          ))
                        ) : (
                          <span className="portal-muted">-</span>
                        )}
                        {hand.participants.length > 4 ? (
                          <small className="portal-muted">+{hand.participants.length - 4} mais</small>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6}>
                    <div className="portal-empty-state">
                      <strong>Nenhuma mao encontrada</strong>
                      <span className="portal-muted">
                        Tente outro termo de busca ou limpe o filtro para ver o historico completo.
                      </span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {removeTarget ? (
        <div className="portal-modal-backdrop" role="dialog" aria-modal="true" onClick={closeRemoveTarget}>
          <div
            className="portal-modal-card portal-remove-player-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="portal-modal-header">
              <div className="portal-modal-title-group">
                <p className="portal-section-card__eyebrow">Remocao manual</p>
                <h3>Remover jogador da mesa</h3>
                <p className="portal-muted">
                  {removeTarget.userName} sera retirado agora e o stack retornara para a carteira do club.
                </p>
              </div>
              <button type="button" className="portal-modal-close" onClick={closeRemoveTarget} aria-label="Fechar modal">
                ×
              </button>
            </div>

            <div className="portal-modal-body">
              <div className="portal-remove-player-modal__summary">
                <span>Jogador selecionado</span>
                <strong>{removeTarget.userName}</strong>
                <small>
                  Stack a devolver: {formatCurrency(removeTarget.currentStack)} · Buy-in total: {formatCurrency(removeTarget.totalBuyIn)}.
                </small>
                <small>A operacao respeita o mesmo fluxo de saida usado no app e sera validada novamente pelo backend.</small>
              </div>

              <div className="portal-remove-player-modal__actions">
                <button
                  type="button"
                  className="portal-secondary-button"
                  onClick={closeRemoveTarget}
                  disabled={Boolean(removingUserId)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="portal-danger-button"
                  onClick={() => void confirmRemovePlayer()}
                  disabled={removingUserId === removeTarget.userId}
                >
                  {removingUserId === removeTarget.userId ? 'Removendo...' : 'Remover agora'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TableDetailPage;
